import React, { useState, useEffect, useMemo } from 'react';
import { Camera, Map, DollarSign, Settings, Lightbulb } from 'lucide-react';
import './ScrollNav.css';

interface NavSection {
  id: string;
  name: string;
  icon: React.ComponentType<{ size?: number }>;
}

const ScrollNav: React.FC = () => {
  const [activeSection, setActiveSection] = useState('gallery');

  const sections: NavSection[] = useMemo(() => [
    { id: 'gallery', name: 'Gallery', icon: Camera },
    { id: 'map', name: 'Map', icon: Map },
    { id: 'pricing', name: 'Pricing', icon: DollarSign },
    { id: 'services', name: 'Services', icon: Settings },
    { id: 'ideas', name: 'Ideas', icon: Lightbulb },
  ], []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100; // Offset for header
      
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial position
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <nav className="scroll-nav">
      <div className="scroll-nav-container">
        {sections.map((section, index) => {
          const IconComponent = section.icon;
          const isActive = activeSection === section.id;
          
          return (
            <div key={section.id} className="scroll-nav-item">
              <button
                className={`scroll-nav-dot ${isActive ? 'active' : ''}`}
                onClick={() => scrollToSection(section.id)}
                aria-label={`Go to ${section.name}`}
              >
                <div className="dot-content">
                  <IconComponent size={16} />
                  <span className="dot-text">{section.name}</span>
                </div>
              </button>
              {index < sections.length - 1 && <div className="scroll-nav-line" />}
            </div>
          );
        })}
      </div>
    </nav>
  );
};

export default ScrollNav; 